<template>
  <canvas
    ref="canvasEl"
    class="signature-canvas"
    :width="width"
    :height="height"
    @touchstart.passive="touchstart"
    @touchmove.prevent="touchmove"
    @touchend="touchend"
    @mousedown="mousedown"
    @mousemove="mousemove"
    @mouseup="mouseup"
  ></canvas>
</template>

<script setup>
import { defineComponent, onMounted, ref, toRefs } from "vue"

defineOptions({ name: "SignaturePad" })

const props = defineProps({
  width: {
    type: Number,
    default: 640,
  },
  height: {
    type: Number,
    default: 320,
  },
  color: {
    type: String,
    default: "#1f2937",
  },
  backgroundColor: {
    type: String,
    default: "#fff",
  },
})

const { width, height, color, backgroundColor } = toRefs(props)
const canvasEl = ref(null)
const cxt = ref(null)
const isDraw = ref(false)

const init = () => {
  if (!canvasEl.value) return
  const context = canvasEl.value.getContext("2d")
  if (!context) return
  cxt.value = context
  context.fillStyle = backgroundColor.value
  context.fillRect(0, 0, canvasEl.value.width, canvasEl.value.height)
  context.strokeStyle = color.value
  context.lineWidth = 2
  context.lineCap = "round"
  context.lineJoin = "round"
  context.shadowBlur = 1
  context.shadowColor = "#ccc"
}

const clear = () => {
  if (!canvasEl.value || !cxt.value) return
  cxt.value.clearRect(0, 0, canvasEl.value.width, canvasEl.value.height)
  cxt.value.fillStyle = backgroundColor.value
  cxt.value.fillRect(0, 0, canvasEl.value.width, canvasEl.value.height)
}

const getPoint = (clientX, clientY) => {
  const rect = canvasEl.value.getBoundingClientRect()
  const scaleX = canvasEl.value.width / rect.width
  const scaleY = canvasEl.value.height / rect.height
  return {
    x: (clientX - rect.left) * scaleX,
    y: (clientY - rect.top) * scaleY,
  }
}

const touchstart = (e) => {
  if (!cxt.value || !canvasEl.value) return
  const touch = e.changedTouches[0]
  const point = getPoint(touch.clientX, touch.clientY)
  cxt.value.beginPath()
  cxt.value.moveTo(point.x, point.y)
}

const touchmove = (e) => {
  if (!cxt.value || !canvasEl.value) return
  const touch = e.changedTouches[0]
  const point = getPoint(touch.clientX, touch.clientY)
  cxt.value.lineTo(point.x, point.y)
  cxt.value.stroke()
}

const touchend = () => {
  cxt.value?.closePath()
}

const mousedown = (e) => {
  if (!cxt.value || !canvasEl.value) return
  const point = getPoint(e.clientX, e.clientY)
  isDraw.value = true
  cxt.value.beginPath()
  cxt.value.moveTo(point.x, point.y)
}

const mousemove = (e) => {
  if (!isDraw.value || !cxt.value || !canvasEl.value) return
  const point = getPoint(e.clientX, e.clientY)
  cxt.value.lineTo(point.x, point.y)
  cxt.value.stroke()
}

const mouseup = () => {
  isDraw.value = false
  cxt.value?.closePath()
}

const getBase64 = (format = "image/png") => {
  if (!canvasEl.value) {
    throw new Error("Canvas not initialized")
  }
  return canvasEl.value.toDataURL(format)
}

onMounted(() => {
  init()
})

defineExpose({ clear, canvasEl, getBase64 })
</script>
